(function () {
  "use strict";

  const targetSelector = "[data-home-cart-animation-target]";
  const mobileQuery = window.matchMedia("(max-width: 1149px)");
  const reducedMotionQuery = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  );

  /**
   * @type {{ items: { map: (arg0: (item: any) => (string | number)[]) => Iterable<readonly [any, any]> | null | undefined; }; } | null}
   */
  let previousCart = null;
  /**
   * @type {number | null}
   */
  let previousCount = null;
  /**
   * @type {{ currentSrc: any; } | null}
   */
  let lastSourceImage = null;
  let hasHandledChange = false;

  function cartTarget() {
    const target = document.querySelector(targetSelector);
    if (!target) return null;
    // PDP sticky cart is display:none from 768px (main-product.css)
    const wrap = target.closest(".hdt-section-toolbar-mobile-cart") || target;
    if (getComputedStyle(wrap).display === "none") return null;
    return target;
  }

  function isEnabled() {
    return mobileQuery.matches && cartTarget();
  }

  /**
   * @param {{ image: any; featured_image: { url: any; src: any; }; }} item
   */
  function itemImage(item) {
    if (!item) return "";
    if (typeof item.image === "string") return item.image;
    if (item.featured_image) {
      return item.featured_image.url || item.featured_image.src || "";
    }
    return "";
  }

  /**
   * @param {{ key: any; variant_id: any; id: any; }} item
   */
  function itemIdentity(item) {
    return String(item && (item.key || item.variant_id || item.id || ""));
  }

  /**
   * @param {{ items: any[]; }} currentCart
   * @param {string} direction
   */
  function changedItem(currentCart, direction) {
    if (!currentCart || !Array.isArray(currentCart.items)) return null;
    if (!previousCart || !Array.isArray(previousCart.items)) {
      return currentCart.items[0] || null;
    }

    const currentById = new Map(
      currentCart.items.map(function (/** @type {{ quantity: any; }} */ item) {
        return [itemIdentity(item), Number(item.quantity) || 0];
      }),
    );
    const previousById = new Map(
      previousCart.items.map(function (/** @type {{ quantity: any; }} */ item) {
        return [itemIdentity(item), Number(item.quantity) || 0];
      }),
    );
    const candidates =
      direction === "add" ? currentCart.items : previousCart.items;

    return (
      candidates.find(function (/** @type {any} */ item) {
        const id = itemIdentity(item);
        const before = previousById.get(id) || 0;
        const after = currentById.get(id) || 0;
        return direction === "add" ? after > before : after < before;
      }) || candidates[0] || null
    );
  }

  /**
   * @param {EventTarget | null} element
   */
  function sourceImageFrom(element) {
    if (!element || !element.closest) return null;
    const product = element.closest(
      "hdt-card-product, .hdt-card-product, [data-product-id], .cart-item, [data-cart-item], hdt-sticky-btn-atc, .hdt-main-product, .hdt-product-info__list, wrapp-hdt-pr-frm",
    );
    if (!product) return null;

    const cardImg = product.querySelector(
      ".hdt-card-product__media--main, [update-img]",
    );
    if (cardImg) return cardImg;

    // PDP: sticky thumb is often hidden on mobile — prefer visible gallery image
    const isPdp =
      product.matches("hdt-sticky-btn-atc, .hdt-main-product, .hdt-product-info__list, wrapp-hdt-pr-frm") ||
      product.closest(".hdt-main-product, hdt-sticky-btn-atc");
    if (isPdp) {
      const galleryImg = document.querySelector(
        ".hdt-product-media__main .hdt-slider__slide.is-selected img, .hdt-product-media__main .hdt-slider__slide:not([hidden]) img, .hdt-product-media__main img",
      );
      if (galleryImg) return galleryImg;
      const stickyImg = document.querySelector(".hdt-sticky-atc__img img");
      if (stickyImg) return stickyImg;
    }

    return product.querySelector("img");
  }

  /**
   * @param {{ isConnected: any; getBoundingClientRect: () => any; }} image
   */
  function imageCenter(image) {
    if (!image || !image.isConnected) return null;
    const rect = image.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  }

  /**
   * Fly only — do NOT call cart blink here.
   * Blink (green → purple fill) is owned solely by Shopify.cartBtnAnimation
   * in custom.js. Calling it again from cart:change / animateProduct resets
   * classes mid-cycle and breaks sync with the nudge.
   *
   * @param {string} imageUrl
   * @param {string} direction
   */
  function animateProduct(imageUrl, direction) {
    const target = cartTarget();
    if (!target) return;

    if (reducedMotionQuery.matches || !imageUrl) return;

    const targetRect = target.getBoundingClientRect();
    const targetCenter = {
      x: targetRect.left + targetRect.width / 2,
      y: targetRect.top + targetRect.height / 2,
    };
    // Always start add-to-cart fly from viewport center (not product image)
    const screenCenter = {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    };
    const sourceCenter = imageCenter(lastSourceImage);
    const start = direction === "add" ? screenCenter : targetCenter;
    const end =
      direction === "add" ? targetCenter : sourceCenter || screenCenter;

    const flyer = document.createElement("img");
    flyer.className =
      "hdt-home-cart-flyer hdt-home-cart-flyer--" + direction;
    flyer.src = imageUrl;
    flyer.alt = "";
    flyer.setAttribute("aria-hidden", "true");
    flyer.style.setProperty("--fly-start-x", start.x + "px");
    flyer.style.setProperty("--fly-start-y", start.y + "px");
    flyer.style.setProperty("--fly-end-x", end.x + "px");
    flyer.style.setProperty("--fly-end-y", end.y + "px");
    document.body.appendChild(flyer);
    flyer.addEventListener(
      "animationend",
      function () {
        flyer.remove();
      },
      { once: true },
    );
  }

  /**
   * @param {{ item_count: any; }} cart
   */
  function handleCart(cart) {
    if (!cart || typeof cart.item_count !== "number") return;

    const currentCount = cart.item_count;
    if (previousCount === null) {
      previousCount = currentCount;
      previousCart = cart;
      return;
    }

    hasHandledChange = true;
    if (
      isEnabled() &&
      !Shopify.isCartDrawerOpen() &&
      currentCount !== previousCount
    ) {
      const direction = currentCount > previousCount ? "add" : "remove";
      const item = changedItem(cart, direction);
      animateProduct(
        itemImage(item) || lastSourceImage?.currentSrc,
        direction,
      );
    }

    previousCount = currentCount;
    previousCart = cart;
    lastSourceImage = null;
  }

  document.addEventListener(
    "pointerdown",
    function (event) {
      lastSourceImage = sourceImageFrom(event.target);
    },
    true,
  );

  document.addEventListener("cart:change", function (event) {
    handleCart(event.detail && event.detail.cart);
  });

  document.addEventListener("cart:refresh", function (event) {
    handleCart(event.detail && event.detail.cart);
  });

  const initialTarget = cartTarget();
  const initialCounter =
    initialTarget && initialTarget.querySelector("hdt-cart-count");
  const initialCount = initialCounter
    ? Number.parseInt(initialCounter.textContent, 10)
    : NaN;
  if (Number.isFinite(initialCount)) previousCount = initialCount;

  fetch("/cart.js")
    .then(function (response) {
      return response.ok ? response.json() : null;
    })
    .then(function (cart) {
      if (!hasHandledChange && cart) {
        previousCart = cart;
        previousCount = cart.item_count;
      }
    })
    .catch(function () {});
})();
